import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Loader2,
  Package as PackageIcon,
  ShieldCheck,
  Zap,
} from "lucide-react";
import GlowCard from "../components/GlowCard";
import { runAwpReview } from "./api";
import { AwpRuleSet, listRuleSets } from "./rulesApi";
import { AwpRun, PACKAGE_TYPE_LABELS, WorkPackage, documentStateOf } from "./types";

interface AwpReviewEngineProps {
  projectId: string;
  packages: WorkPackage[];
  extractorVersion: number;
  run: AwpRun | null;
  admin?: boolean;
  onRun: (run: AwpRun) => void;
  onGoToDocuments: () => void;
  onGoToFindings: () => void;
}

const toggle = (list: string[], id: string) =>
  list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

/**
 * The AWP Review Engine: the packages on the left, the standards on the right, checked against
 * each other and against those standards' rules.
 *
 * THIS SCREEN IS THE PICKER AND THE RECEIPT, NOT THE RESULT. Running the check leaves a short
 * summary of what was checked and sends the reviewer to Findings — the same shape as the
 * engineering flow. The rule-by-rule register lives behind a toggle here, because 260 rows is not
 * a summary, and the findings themselves are not shown here at all.
 *
 * NO PROGRESS BAR, DELIBERATELY. The engineering Compliance Engine streams because it is making
 * model calls that take minutes. This makes none: the comparisons run over data already extracted
 * and finish in milliseconds. A progress bar on a job that is over before the first frame is a lie
 * about what the software is doing — see the Document AI download, where a bar that filled
 * instantly hid a server buffering the whole file.
 */
const AwpReviewEngine = ({
  projectId,
  packages,
  extractorVersion,
  run,
  admin = false,
  onRun,
  onGoToDocuments,
  onGoToFindings,
}: AwpReviewEngineProps) => {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [standards, setStandards] = useState<AwpRuleSet[] | null>(null);
  const [pickedPkgs, setPickedPkgs] = useState<string[]>([]);
  const [pickedSets, setPickedSets] = useState<string[]>([]);

  const readable = useMemo(
    () => packages.filter((p) => documentStateOf(p, extractorVersion) !== "none" && p.read),
    [packages, extractorVersion],
  );
  const unread = packages.length - readable.length;

  // Everything readable starts selected: the common case is "check the lot", and a reviewer who
  // wants a subset is deselecting, not hunting for a Select all.
  useEffect(() => {
    setPickedPkgs((prev) => (prev.length ? prev : readable.map((p) => p.id)));
  }, [readable]);

  useEffect(() => {
    let alive = true;
    listRuleSets()
      .then((sets) => {
        if (!alive) return;
        const usable = sets.filter((s) => s.status !== "retired");
        setStandards(usable);
        setPickedSets((prev) => {
          if (prev.length) return prev;
          const last = run?.ruleSets?.map((s) => s.id).filter((id) => usable.some((u) => u.id === id));
          if (last?.length) return last;
          // One standard in the library and nothing chosen yet — choose it.
          return usable.length === 1 ? [usable[0].id] : [];
        });
      })
      .catch(() => alive && setStandards([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = async () => {
    setRunning(true);
    setError(null);
    try {
      onRun(await runAwpReview(projectId, { packageIds: pickedPkgs, ruleSetIds: pickedSets }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "The review failed.");
    } finally {
      setRunning(false);
    }
  };

  if (readable.length === 0) {
    return (
      <GlowCard hover={false}>
        <div className="vr-soon">
          <div className="vr-soon-icon">
            <ShieldCheck size={28} />
          </div>
          <h2 className="vr-h-sec">Nothing read yet</h2>
          <p className="vr-muted" style={{ maxWidth: 480 }}>
            The review compares packages against each other and against a standard, so it needs
            their documents read first. Go to Documents and press Read all.
          </p>
          <button className="vr-btn vr-btn-outline" onClick={onGoToDocuments}>
            Go to Documents <ArrowRight size={14} />
          </button>
        </div>
      </GlowCard>
    );
  }

  const cov = run?.coverage;

  return (
    <div className="vr-awp-review">
      {error && <div className="vr-form-error">{error}</div>}

      <div className="vr-grid vr-grid-2">
        <GlowCard className="vr-pick-card" hover={false}>
          <div className="vr-table-head">
            <div className="vr-card-head">
              <PackageIcon size={14} />
              <h3>
                Work Packages{" "}
                <span className="vr-cell-code">
                  ({pickedPkgs.length}/{readable.length})
                </span>
              </h3>
            </div>
            {!admin && (
              <button
                className="vr-linkbtn"
                onClick={() =>
                  setPickedPkgs(
                    pickedPkgs.length === readable.length ? [] : readable.map((p) => p.id),
                  )
                }
              >
                {pickedPkgs.length === readable.length && pickedPkgs.length > 0
                  ? "Clear all"
                  : "Select all"}
              </button>
            )}
          </div>

          <div className="vr-pick-list">
            {readable.map((pkg) => (
              <label key={pkg.id} className="vr-pick-row">
                <input
                  type="checkbox"
                  checked={pickedPkgs.includes(pkg.id)}
                  disabled={admin}
                  onChange={() => setPickedPkgs((prev) => toggle(prev, pkg.id))}
                />
                <span className="vr-pick-body">
                  <span className="vr-pick-name">
                    {pkg.packageNo} — {pkg.title}
                  </span>
                  <span className="vr-pick-meta">
                    {PACKAGE_TYPE_LABELS[pkg.type]}
                    {pkg.discipline ? ` · ${pkg.discipline}` : ""}
                    {pkg.read ? ` · ${pkg.read.counts.tables} tables read` : ""}
                  </span>
                  {documentStateOf(pkg, extractorVersion) === "stale" && (
                    <span className="vr-pick-warn">
                      Read by an older reader — read it again for the full picture
                    </span>
                  )}
                </span>
              </label>
            ))}
            {unread > 0 && (
              <div className="vr-empty">
                {unread} {unread === 1 ? "package has" : "packages have"} no document read and
                cannot be checked.{" "}
                <button className="vr-linkbtn" onClick={onGoToDocuments}>
                  Go to Documents
                </button>
              </div>
            )}
          </div>
        </GlowCard>

        <GlowCard className="vr-pick-card" hover={false}>
          <div className="vr-table-head">
            <div className="vr-card-head">
              <BookOpen size={14} />
              <h3>
                Governing Standards{" "}
                <span className="vr-cell-code">
                  ({pickedSets.length}/{standards?.length ?? 0})
                </span>
              </h3>
            </div>
            {!admin && (standards?.length ?? 0) > 1 && (
              <button
                className="vr-linkbtn"
                onClick={() =>
                  setPickedSets(
                    pickedSets.length === standards!.length ? [] : standards!.map((s) => s.id),
                  )
                }
              >
                {pickedSets.length === standards!.length ? "Clear all" : "Select all"}
              </button>
            )}
          </div>

          <div className="vr-pick-list">
            {(standards ?? []).map((std) => (
              <label key={std.id} className="vr-pick-row">
                <input
                  type="checkbox"
                  checked={pickedSets.includes(std.id)}
                  disabled={admin}
                  onChange={() => setPickedSets((prev) => toggle(prev, std.id))}
                />
                <span className="vr-pick-body">
                  <span className="vr-pick-name">{std.title}</span>
                  <span className="vr-pick-meta">
                    {[std.docNo, std.version, `${std.ruleCount} rules`].filter(Boolean).join(" · ")}
                  </span>
                  {std.status === "draft" && (
                    <span className="vr-pick-warn">Draft — not yet in force on this project</span>
                  )}
                </span>
              </label>
            ))}
            {standards === null && <div className="vr-empty">Loading the rule library…</div>}
            {standards !== null && standards.length === 0 && (
              <div className="vr-empty">
                The AWP rule library is empty. Import a technical standard under Admin → Standards
                to check packages against it.
              </div>
            )}
          </div>
        </GlowCard>
      </div>

      <GlowCard hover={false} className="vr-awp-run-card">
        <div className="vr-card-head">
          <ShieldCheck size={14} />
          <h3>Run the check</h3>
          {!admin && (
            <button
              className="vr-btn vr-btn-glow"
              onClick={() => void go()}
              disabled={running || pickedPkgs.length === 0}
              style={{ marginLeft: "auto" }}
            >
              {running ? (
                <>
                  <Loader2 size={14} className="vr-spin-inline" /> Checking…
                </>
              ) : (
                <>
                  <Zap size={14} /> {run ? "Check again" : "Check packages"}
                </>
              )}
            </button>
          )}
        </div>

        <div className="vr-awp-run-facts">
          <span>
            <strong>{pickedPkgs.length}</strong>{" "}
            {pickedPkgs.length === 1 ? "package" : "packages"} selected
          </span>
          {pickedPkgs.length < readable.length && (
            <span className="vr-awp-run-warn">
              <AlertTriangle size={13} /> {readable.length - pickedPkgs.length} readable{" "}
              {readable.length - pickedPkgs.length === 1 ? "package" : "packages"} left out — a
              cross-package rule cannot see what is not in the run
            </span>
          )}
          {unread > 0 && (
            <span className="vr-awp-run-warn">
              <AlertTriangle size={13} /> {unread} unread
            </span>
          )}
          {run && (
            <span>
              last run {new Date(run.runAt).toLocaleString()}
              {run.durationMs !== undefined && ` · ${run.durationMs} ms`}
              {run.ruleSets?.length
                ? ` · ${run.ruleSets.map((s) => s.docNo || s.title).join(" · ")}`
                : ""}
            </span>
          )}
        </div>
      </GlowCard>

      {/* What was checked, and what it left alone. The findings themselves are on the next step. */}
      {run && (
        <GlowCard hover={false} className="vr-awp-summary">
          <div className="vr-card-head">
            <CheckCircle2 size={14} className="vr-pass-ico" />
            <h3>Checked</h3>
            <button
              className="vr-btn vr-btn-sm vr-btn-glow"
              onClick={onGoToFindings}
              style={{ marginLeft: "auto" }}
            >
              {run.findings.length > 0
                ? `See ${run.findings.length} ${run.findings.length === 1 ? "finding" : "findings"}`
                : "Go to Findings"}
              <ArrowRight size={13} />
            </button>
          </div>

          <div className="vr-awp-summary-rows">
            <div className="vr-awp-summary-row">
              <span className="vr-awp-summary-label">Packages checked</span>
              <span className="vr-awp-summary-value">
                {run.packageIds.length}
                {run.excluded.length > 0 && (
                  <em className="vr-awp-skip"> · {run.excluded.length} left out</em>
                )}
              </span>
            </div>

            {run.ruleSets?.map((set) => (
              <div key={set.id} className="vr-awp-summary-row">
                <span className="vr-awp-summary-label">Standard</span>
                <span className="vr-awp-summary-value">
                  {set.docNo ? `${set.docNo} — ` : ""}
                  {set.title}
                  {set.version ? <em className="vr-awp-skip"> · {set.version}</em> : null}
                </span>
              </div>
            ))}

            {cov ? (
              <div className="vr-awp-summary-row">
                <span className="vr-awp-summary-label">Rules checked</span>
                <span className="vr-awp-summary-value">
                  {cov.automated} of {cov.total}
                  <em className="vr-awp-skip">
                    {" "}
                    · {cov.passed} pass, {cov.failed} with findings
                    {cov.noData > 0 && `, ${cov.noData} with nothing in the packages to test`}
                  </em>
                </span>
              </div>
            ) : (
              <div className="vr-awp-summary-row">
                <span className="vr-awp-summary-label">Checks run</span>
                <span className="vr-awp-summary-value">
                  {run.rules.filter((r) => !r.skipped).length} of {run.rules.length}
                  <em className="vr-awp-skip">
                    {" "}
                    · no standard selected, so coverage of one cannot be reported
                  </em>
                </span>
              </div>
            )}

            <div className="vr-awp-summary-row">
              <span className="vr-awp-summary-label">Findings</span>
              <span className={`vr-awp-summary-value${run.findings.length ? " vr-crit" : ""}`}>
                {run.findings.length}
              </span>
            </div>
          </div>

          {run.excluded.length > 0 && (
            <div className="vr-awp-readout vr-awp-readout-warn">
              <AlertTriangle size={14} />
              <span>
                Left out: {run.excluded.map((e) => `${e.packageNo} (${e.reason})`).join("; ")}
              </span>
            </div>
          )}
        </GlowCard>
      )}
    </div>
  );
};

export default AwpReviewEngine;
